// withdraw-records.tsx — 页面-提现记录(user.pen frame CUGMr)
//
// 设计:user.pen frame CUGMr — 自己的提现申请列表,最新在上;空态显示"还没有提现记录"。
//
// 数据源:RLS 直读 public.withdraw(withdraw_select_self:user_id = auth.uid();
// 详 database/migrations/20260516001000_rls_and_grants.sql)。
//
// 路由:`/wallet/withdraw/records`(withdraw.tsx 顶部"提现记录"按钮跳过来)。
import { useQuery } from '@tanstack/react-query'
import { getSupabase, useCurrentUser } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type WithdrawStatus =
  | 'pending'
  | 'awaiting_review'
  | 'approved'
  | 'completed'
  | 'rejected'
  | 'cancelled'

type WithdrawRow = {
  id: string
  amount_coin: number
  payout_method: string
  payout_contact: string
  status: WithdrawStatus
  reject_reason: string | null
  tx_hash: string | null
  created_at: string
  updated_at: string
}

const STATUS_LABEL: Record<WithdrawStatus, string> = {
  pending: '待邮件确认',
  awaiting_review: '审核中',
  approved: '已通过',
  completed: '已到账',
  rejected: '已拒绝',
  cancelled: '已取消',
}

const STATUS_TONE: Record<WithdrawStatus, string> = {
  pending: 'bg-amber-50 text-amber-600',
  awaiting_review: 'bg-blue-50 text-blue-600',
  approved: 'bg-blue-50 text-blue-600',
  completed: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-red-50 text-red-600',
  cancelled: 'bg-gray-100 text-gray-500',
}

const PAYOUT_LABEL: Record<string, string> = {
  alipay: '支付宝',
  wechat: '微信',
  bank_card: '银行卡',
  usdt: 'USDT',
  usdc: 'USDC',
  btc: 'BTC',
  other: '其他',
}

function useWithdrawRecords(myUserId: string | undefined) {
  return useQuery({
    queryKey: ['wallet', 'withdraw', 'records', myUserId] as const,
    enabled: !!myUserId,
    queryFn: async (): Promise<WithdrawRow[]> => {
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- withdraw RLS owner 收口(只读自己的提现)
      const { data, error } = await sb
        .from('withdraw')
        .select('id, amount_coin, payout_method, payout_contact, status, reject_reason, tx_hash, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw error
      return (data ?? []) as WithdrawRow[]
    },
    staleTime: 30_000,
  })
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function maskContact(contact: string): string {
  if (contact.length <= 4) return contact
  // alipay 邮箱 / 微信号 / 银行卡 / 钱包地址 都按"前4+***+后4"脱敏
  return `${contact.slice(0, 4)}***${contact.slice(-4)}`
}

export function WithdrawRecordsPage() {
  const { user } = useCurrentUser()
  const { data: rows = [], isLoading } = useWithdrawRecords(user?.user_id)

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar title="提现记录" />
        <main data-testid="CUGMr" className="flex flex-1 flex-col gap-2 px-4 pb-8">
          {isLoading ? (
            <div className="py-12 text-center text-[13px] text-gray-400">加载中…</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-[13px] text-gray-400">还没有提现记录</div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="flex flex-col gap-2 rounded-xl bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-semibold tabular-nums text-[#1A1A1A]">
                    -{r.amount_coin.toLocaleString()} COIN
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[r.status]}`}>
                    {STATUS_LABEL[r.status]}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12px] text-gray-500">
                  <span>
                    {PAYOUT_LABEL[r.payout_method] ?? r.payout_method} · {maskContact(r.payout_contact)}
                  </span>
                  <span>{formatDate(r.created_at)}</span>
                </div>
                {r.status === 'rejected' && r.reject_reason && (
                  <p className="text-[12px] text-red-500">拒绝原因:{r.reject_reason}</p>
                )}
                {r.status === 'completed' && r.tx_hash && (
                  <p className="break-all text-[11px] text-gray-400">tx:{r.tx_hash}</p>
                )}
              </div>
            ))
          )}
        </main>
      </MobileScreen>
    </AppLayout>
  )
}
